// Generates the structured RAMS *preview* shown on each library page: scope, method
// sequence, the 5x5 hazard/risk table, PPE and competence. Content is composed from
// category hazard banks below — sound, recognised control measures, but a DRAFT a
// competent person must review against current HSE guidance for the specific site.
// The full fillable document is what early-access unlocks.

import type { CategoryKey, TemplateSeed } from "./library";
import { TEMPLATE_SPECIFIC } from "./templateSpecific";

export interface RiskRating {
  likelihood: number; // 1-5
  severity: number; // 1-5
}
export interface HazardRow {
  hazard: string;
  who: string[];
  initial: RiskRating;
  controls: string[];
  residual: RiskRating;
}
export interface TemplateContent {
  scope: string;
  legislation: string[];
  sequence: string[];
  hazards: HazardRow[];
  ppe: string[];
  competence: string[];
  permits: string[];
}

export function riskScore(r: RiskRating): number {
  return r.likelihood * r.severity;
}
export function riskBand(score: number): "low" | "medium" | "high" | "very-high" {
  if (score <= 4) return "low";
  if (score <= 9) return "medium";
  if (score <= 15) return "high";
  return "very-high";
}
export const RISK_BAND_LABEL: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  "very-high": "Very high",
};

const UNIVERSAL: HazardRow[] = [
  {
    hazard: "Manual handling of materials and equipment",
    who: ["Operatives"],
    initial: { likelihood: 3, severity: 3 },
    controls: [
      "Mechanical handling/aids used where reasonably practicable",
      "Loads assessed; team lifts for awkward or heavy items",
      "Operatives briefed on safe lifting technique",
    ],
    residual: { likelihood: 1, severity: 3 },
  },
  {
    hazard: "Slips, trips and falls on the level",
    who: ["Operatives", "Other trades"],
    initial: { likelihood: 3, severity: 2 },
    controls: [
      "Work area kept clear; good housekeeping enforced",
      "Cables and hoses routed away from walkways",
      "Spillages cleaned immediately; adequate lighting maintained",
    ],
    residual: { likelihood: 1, severity: 2 },
  },
];

// Partial: categories added after this bank (building-services, fit-out) reuse the
// nearest existing bank via CATEGORY_FALLBACK. This is only the generic fallback —
// real bodies come from templateSpecific / composed payloads, and bodyless entries
// are noindex'd, so a near-neighbour fallback here is acceptable.
const CATEGORY: Partial<Record<
  CategoryKey,
  { hazards: HazardRow[]; sequence: string[]; ppe: string[]; competence: string[]; permits: string[]; legislation: string[] }
>> = {
  "work-at-height": {
    hazards: [
      {
        hazard: "Fall from height (edge, opening or access equipment)",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Avoid work at height where reasonably practicable; otherwise use a safe work platform",
          "Edge protection / guardrails to open edges and openings",
          "Access equipment inspected before use and at statutory intervals",
          "Collective protection prioritised over personal fall-arrest",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Falling objects / tools striking persons below",
        who: ["Operatives", "Public", "Other trades"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Exclusion zone below the work; toe-boards and brick guards fitted",
          "Tools tethered; materials not stored at open edges",
          "Banksman/signage where the public could be affected",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    sequence: [
      "Confirm access equipment is the right type for the task and ground conditions",
      "Inspect platform/scaffold/MEWP and record pre-use checks",
      "Establish exclusion zone and edge protection before work begins",
      "Carry out the work, maintaining three points of contact / safe platform discipline",
      "Lower materials under control; clear and inspect the area on completion",
    ],
    ppe: ["Hard hat", "Safety boots", "Hi-vis", "Gloves", "Fall-arrest harness where collective protection is not reasonably practicable"],
    competence: ["Working at height awareness", "PASMA / IPAF / CISRS as appropriate to the equipment", "Harness and rescue training where fall-arrest is used"],
    permits: ["Permit to work where required by the principal contractor"],
    legislation: ["Work at Height Regulations 2005", "CDM 2015", "PUWER 1998", "LOLER 1998 (for MEWPs)"],
  },
  electrical: {
    hazards: [
      {
        hazard: "Electric shock / electrocution from live conductors",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Safe isolation procedure followed; prove dead with an approved voltage indicator",
          "Lock-off and caution notices applied; isolation under the control of the competent person",
          "Live working only where unavoidable and with a specific risk assessment and permit",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Arc flash / burns during testing or fault",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Appropriate test equipment to GS38; insulated tools used",
          "Arc-rated PPE where an arc-flash risk remains",
          "Work area secured against inadvertent re-energisation",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    sequence: [
      "Identify the circuit and confirm the point of isolation",
      "Isolate, lock off and prove dead before any work begins",
      "Carry out installation/testing using calibrated, GS38-compliant equipment",
      "Inspect, test and certify the installation",
      "Re-energise under control; remove locks and update records",
    ],
    ppe: ["Safety boots", "Hi-vis", "Insulating gloves where required", "Arc-rated PPE where applicable", "Eye protection"],
    competence: ["Qualified electrician (e.g. 18th Edition)", "Safe isolation training", "Test instrument competence (GS38)"],
    permits: ["Permit to work for live or near-live working"],
    legislation: ["Electricity at Work Regulations 1989", "BS 7671 (18th Edition)", "CDM 2015", "HSG85 Electricity at Work"],
  },
  groundworks: {
    hazards: [
      {
        hazard: "Collapse of excavation / trench sides",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Support system (trench box/shoring) or battering to a safe angle",
          "Spoil and plant kept back from the edge",
          "Daily inspection by a competent person and after any event affecting stability",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Contact with underground services",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Service drawings obtained; CAT & Genny scan before and during the dig",
          "Hand-dig trial holes to locate and expose services",
          "Safe digging practices (HSG47) followed throughout",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
    ],
    sequence: [
      "Obtain and review service drawings; scan and mark the area",
      "Set out exclusion zones and install support/battering as designed",
      "Excavate progressively, hand-digging near located services",
      "Inspect supports and atmosphere before any entry",
      "Backfill and reinstate; remove plant under banksman control",
    ],
    ppe: ["Hard hat", "Safety boots", "Hi-vis", "Gloves", "Eye protection"],
    competence: ["Competent excavation supervisor", "CAT & Genny operator training", "Confined space training where applicable"],
    permits: ["Permit to dig", "Confined space entry permit where applicable"],
    legislation: ["CDM 2015", "HSG47 Avoiding danger from underground services", "Confined Spaces Regulations 1997"],
  },
  "lifting-plant": {
    hazards: [
      {
        hazard: "Contact between plant and people",
        who: ["Operatives", "Public", "Other trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Segregation of plant and pedestrians; barriers and routes established",
          "Trained banksman/signaller; 360° visibility aids and reversing alarms",
          "Exclusion zones around the operating radius",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Overturn / failure during lifting operations",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Lift planned by an appointed person; lifting plan and SWL checked",
          "Ground bearing assessed; outriggers/mats used",
          "LOLER thorough examination and pre-use checks recorded",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    sequence: [
      "Plan the lift / movement; confirm ground conditions and SWL",
      "Establish exclusion zones and pedestrian segregation",
      "Carry out pre-use checks on plant and lifting accessories",
      "Operate under banksman control within the planned envelope",
      "Stand plant down safely; record inspections",
    ],
    ppe: ["Hard hat", "Safety boots", "Hi-vis", "Gloves"],
    competence: ["CPCS/NPORS plant operator", "Appointed person / slinger-signaller for lifts", "Banksman training"],
    permits: ["Lifting plan", "Permit for lifts over occupied areas"],
    legislation: ["LOLER 1998", "PUWER 1998", "CDM 2015", "BS 7121 (lifting operations)"],
  },
  "hot-works": {
    hazards: [
      {
        hazard: "Fire / ignition of combustible materials",
        who: ["Operatives", "Other trades", "Building occupants"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Hot works permit; combustibles removed or protected within the work area",
          "Suitable extinguisher to hand; fire watch maintained during and after work",
          "60-minute post-work fire watch before leaving the area",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Fume, UV radiation and burns",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Local exhaust ventilation / fume extraction where required",
          "Screens to protect others from arc/UV; appropriate eye protection",
          "Flame-resistant clothing and gauntlets worn",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
    ],
    sequence: [
      "Issue hot works permit and brief the team",
      "Clear/protect combustibles and set up fire watch and extinguishers",
      "Set up extraction and screening; carry out the hot work",
      "Maintain fire watch during the task",
      "Inspect and maintain a 60-minute fire watch after completion",
    ],
    ppe: ["Welding/face shield", "Flame-resistant clothing", "Gauntlets", "Safety boots", "Respiratory protection where required"],
    competence: ["Competent welder/operative for the process", "Hot works permit awareness", "Fire watch briefing"],
    permits: ["Hot works permit"],
    legislation: ["Regulatory Reform (Fire Safety) Order 2005", "COSHH 2002 (fume)", "CDM 2015", "PUWER 1998"],
  },
  "coshh-substances": {
    hazards: [
      {
        hazard: "Exposure to hazardous dust / substances",
        who: ["Operatives", "Other trades"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "COSHH assessment completed; safer product substituted where possible",
          "Water suppression / on-tool extraction (LEV) to control dust at source",
          "RPE to the correct protection factor; face-fit tested",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Skin contact / dermatitis and burns",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Chemical-resistant gloves and barrier cream; skin checks",
          "Decontamination and welfare facilities available",
          "Spill kit and safety data sheets on site",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
    ],
    sequence: [
      "Review safety data sheets and complete the COSHH assessment",
      "Set up controls (LEV, suppression, ventilation) before work",
      "Carry out the task using RPE and PPE as assessed",
      "Decontaminate and store/dispose of substances correctly",
      "Maintain health surveillance records where required",
    ],
    ppe: ["RPE (face-fit tested)", "Chemical-resistant gloves", "Coveralls", "Eye protection", "Safety boots"],
    competence: ["COSHH awareness", "Face-fit tested for RPE", "LEV use and checks"],
    permits: ["Permit for high-risk substances where required"],
    legislation: ["COSHH 2002", "Control of Lead at Work 2002 (where relevant)", "CDM 2015", "HSE WIS guidance"],
  },
  "demolition-strip": {
    hazards: [
      {
        hazard: "Premature / uncontrolled structural collapse",
        who: ["Operatives", "Public", "Other trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Demolition sequence designed by a competent person; structural survey reviewed",
          "Exclusion zones and monitoring during the works",
          "Top-down, controlled sequence; no undermining of supporting elements",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Disturbance of asbestos-containing materials (ACMs)",
        who: ["Operatives", "Other trades"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Refurbishment & demolition asbestos survey reviewed before work",
          "Stop work and report if suspected ACMs are found",
          "Licensed contractor engaged for licensable asbestos work",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    sequence: [
      "Review structural and asbestos surveys; agree the sequence",
      "Isolate services and set up exclusion zones",
      "Soft strip, then demolish in the planned controlled sequence",
      "Suppress dust and manage waste segregation",
      "Inspect the area and confirm stability throughout",
    ],
    ppe: ["Hard hat", "Safety boots", "Hi-vis", "Gloves", "RPE and eye protection for dust"],
    competence: ["Competent demolition supervisor (e.g. CCDO)", "Asbestos awareness (UKATA/IATP)", "Plant operator certification"],
    permits: ["Permit to work", "Asbestos notification where licensable"],
    legislation: ["CDM 2015", "Control of Asbestos Regulations 2012", "BS 6187 (demolition)", "COSHH 2002"],
  },
  "trades-finishing": {
    hazards: [
      {
        hazard: "Use of power tools (cuts, entanglement, HAVS)",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "PUWER pre-use checks; guards in place and tools maintained",
          "Tool trigger-time managed to control hand-arm vibration",
          "Cut-resistant gloves and eye protection used",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
      {
        hazard: "Dust and fumes from cutting / mixing / coatings",
        who: ["Operatives", "Other trades"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "On-tool extraction or water suppression for cutting",
          "Ventilation when applying solvent-based products; RPE as assessed",
          "Materials stored and handled per the safety data sheet",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
    ],
    sequence: [
      "Set out the work area and protect adjacent finishes/occupants",
      "Pre-use check tools and set up dust/fume controls",
      "Carry out the work in the planned sequence",
      "Clean down, manage waste and store materials safely",
      "Inspect and hand over the area",
    ],
    ppe: ["Safety boots", "Eye protection", "Cut-resistant gloves", "RPE for dust/fumes", "Knee pads where applicable"],
    competence: ["Trade competence / relevant CSCS card", "Tool-specific training", "COSHH awareness"],
    permits: ["Permit to work where required by the principal contractor"],
    legislation: ["PUWER 1998", "COSHH 2002", "Control of Vibration at Work 2005", "CDM 2015"],
  },
  "site-general": {
    hazards: [
      {
        hazard: "Site traffic and moving vehicles",
        who: ["Operatives", "Public", "Visitors"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Segregated vehicle and pedestrian routes; clear signage",
          "Speed limits and banksman control for reversing",
          "Hi-vis worn at all times in the working area",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
      {
        hazard: "Inadequate welfare / lone working",
        who: ["Operatives"],
        initial: { likelihood: 2, severity: 3 },
        controls: [
          "Welfare facilities provided per Schedule 2 of CDM 2015",
          "Lone-working check-in procedure and means of raising the alarm",
          "First aid provision appropriate to the site",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
    ],
    sequence: [
      "Set up site access, welfare and signage",
      "Establish traffic management and pedestrian routes",
      "Brief all operatives at induction",
      "Maintain housekeeping and welfare throughout",
      "Review arrangements as the site changes",
    ],
    ppe: ["Hard hat", "Safety boots", "Hi-vis", "Gloves", "Eye protection as required"],
    competence: ["Site induction", "CSCS card", "First aider on site", "Relevant supervisor competence"],
    permits: ["Permit to work where required"],
    legislation: ["CDM 2015", "Management of Health and Safety at Work Regs 1999", "Workplace (Health, Safety and Welfare) Regs 1992"],
  },
};

export function buildTemplateContent(t: TemplateSeed): TemplateContent {
  // Per-template authored content (lib/templateSpecific/) — every seed template
  // has an entry. The category banks below remain only as a fallback for
  // templates added before their specific content is written.
  const s = TEMPLATE_SPECIFIC[t.id];
  if (s) {
    return {
      scope: s.scope,
      legislation: s.legislation,
      sequence: s.sequence,
      hazards: s.hazards,
      ppe: s.ppe,
      competence: s.competence,
      permits: s.permits,
    };
  }
  const CATEGORY_FALLBACK: Partial<Record<CategoryKey, CategoryKey>> = {
    "building-services": "electrical",
    "fit-out": "trades-finishing",
  };
  const c = CATEGORY[t.category] ?? CATEGORY[CATEGORY_FALLBACK[t.category] ?? "site-general"]!;
  return {
    scope: `This RAMS covers ${t.task.toLowerCase()} carried out by ${t.trade.toLowerCase()} operatives on a UK construction or maintenance site. It sets out the method, the principal hazards and the control measures to be applied. Site-specific details (address, client, access, dates and supervisor) are completed for each job before the document is issued for principal-contractor review.`,
    legislation: c.legislation,
    sequence: c.sequence,
    hazards: [...c.hazards, ...UNIVERSAL],
    ppe: c.ppe,
    competence: c.competence,
    permits: c.permits,
  };
}
